import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { EMPTY, Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export type SharePlatform =
  | 'reddit' | 'facebook' | 'twitter' | 'pinterest'
  | 'whatsapp' | 'messenger' | 'email' | 'copy';

export interface ShareConfig {
  contentId: string;
  contentType: 'post' | 'blog' | 'event';
  title: string;
  description?: string;
  imageUrl?: string;
}

@Injectable({ providedIn: 'root' })
export class ShareService {
  private http = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);
  private baseUrl = `${environment.apiUrl}/share`;

  /** Record a share on the backend — increments count */
  record(contentId: string): Observable<any> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;
    return this.http.post(`${this.baseUrl}/${contentId}`, {});
  }

  /** Build the public URL for the content */
  buildUrl(config: ShareConfig): string {
    if (!isPlatformBrowser(this.platformId)) return '';
    const base = window.location.origin;
    const paths: Record<string, string> = {
      post:  `/post/${config.contentId}`,
      blog:  `/blog/${config.contentId}`,
      event: `/event/${config.contentId}`,
    };
    return `${base}${paths[config.contentType]}`;
  }

  /** Open the share URL for a given platform */
  openPlatform(platform: SharePlatform, config: ShareConfig): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const url   = encodeURIComponent(this.buildUrl(config));
    const title = encodeURIComponent(config.title);
    const desc  = encodeURIComponent(config.description ?? config.title);
    const img   = encodeURIComponent(config.imageUrl ?? '');

    const targets: Record<SharePlatform, string | null> = {
      reddit:    `https://www.reddit.com/submit?url=${url}&title=${title}`,
      facebook:  `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter:   `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
      pinterest: `https://pinterest.com/pin/create/button/?url=${url}&description=${desc}&media=${img}`,
      whatsapp:  `https://api.whatsapp.com/send?text=${title}%20${url}`,
      messenger: `https://www.facebook.com/dialog/send?link=${url}&app_id=YOUR_FB_APP_ID&redirect_uri=${url}`,
      email:     `mailto:?subject=${title}&body=${desc}%0A%0A${url}`,
      copy:      null,
    };

    if (platform === 'copy') {
      navigator.clipboard.writeText(decodeURIComponent(url)).catch(() => {});
      return;
    }

    const target = targets[platform];
    if (target) window.open(target, '_blank', 'noopener,noreferrer,width=600,height=500');
  }
}
